import { useCallback, useImperativeHandle, useState, forwardRef } from 'react';
import { Container, Row } from 'reactstrap';
import { Modal, Button } from '@octano/global-ui';
import { ClassroomApiResponse } from '../../types';
import { FutureReservationsContent } from './content/FutureReservationsContent';
import useTexts from '../../hooks/useTexts';
import { ConfirmDisableContent } from './content/ConfirmDisableContent';

interface DisableDetailsModalProps {
  onConfirmDisable: (classroom: ClassroomApiResponse) => Promise<void>;
}

export interface DisableDetailsModalMethods {
  open: (classroom: ClassroomApiResponse) => void;
  close: () => void;
}

const DisableDetailsModal = forwardRef<
  DisableDetailsModalMethods,
  DisableDetailsModalProps
>(({ onConfirmDisable }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [classroom, setClassroom] = useState<ClassroomApiResponse | null>(null);
  const [isDisabling, setIsDisabling] = useState(false);
  const texts = useTexts();

  const open = useCallback((classroomData: ClassroomApiResponse) => {
    setClassroom(classroomData);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    if (isDisabling) {
      return;
    }
    setIsOpen(false);
    setClassroom(null);
  }, [isDisabling]);

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const handleConfirmDisable = useCallback(async () => {
    if (classroom && !isDisabling) {
      setIsDisabling(true);
      try {
        await onConfirmDisable(classroom);
        close();
      } catch (error) {
      } finally {
        setIsDisabling(false);
      }
    }
  }, [classroom, isDisabling, onConfirmDisable, close]);

  const renderContent = () => {
    if (!classroom) {
      return null;
    }

    // Check if there are current or future reservations
    if (
      classroom.currentOrFutureReservations.lessons.length > 0 ||
      classroom.currentOrFutureReservations.events.length > 0
    ) {
      return (
        <FutureReservationsContent
          lessons={classroom.currentOrFutureReservations.lessons}
          events={classroom.currentOrFutureReservations.events}
          isDisabling={true}
        />
      );
    }

    return <ConfirmDisableContent />;
  };

  const canToggleStatus =
    classroom &&
    (!classroom.isEnabled ||
      (classroom.currentOrFutureReservations.lessons.length === 0 &&
        classroom.currentOrFutureReservations.events.length === 0));

  const modalSize = canToggleStatus ? 'md' : 'lg';

  return (
    <Modal isOpen={isOpen} size={modalSize} toggle={close}>
      <Container fluid>
        {renderContent()}

        <Row className="justify-content-center" style={{ marginTop: '50px' }}>
          <Button
            onClick={close}
            text={
              canToggleStatus
                ? texts.disable.buttons.cancel
                : texts.delete.buttons.ok
            }
            outlined
            disabled={isDisabling}
            style={canToggleStatus ? { width: '200px' } : { width: '400px' }}
          />

          {canToggleStatus && (
            <Button
              onClick={handleConfirmDisable}
              text={
                classroom?.isEnabled
                  ? texts.disable.buttons.disable
                  : 'Habilitar'
              }
              loading={isDisabling}
              disabled={isDisabling}
              style={{ width: '200px', marginLeft: '20px' }}
            />
          )}
        </Row>
      </Container>
    </Modal>
  );
});

export default DisableDetailsModal;
